/**
 * Secure Storage Utility
 * Encrypts sensitive data before storing in localStorage to prevent unauthorized access
 * Uses Web Crypto API for AES-GCM encryption
 */

class SecureStorage {
    /**
     * Derives an encryption key from a password using PBKDF2
     * @param {string} password - The password to derive the key from
     * @param {Uint8Array} salt - The salt for key derivation
     * @returns {Promise<CryptoKey>} The derived encryption key
     */
    static async deriveKey(password, salt) {
        const encoder = new TextEncoder();
        const keyMaterial = await crypto.subtle.importKey(
            'raw',
            encoder.encode(password),
            { name: 'PBKDF2' },
            false,
            ['deriveBits', 'deriveKey']
        );

        return crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt: salt,
                iterations: 100000,
                hash: 'SHA-256'
            },
            keyMaterial,
            { name: 'AES-GCM', length: 256 },
            false,
            ['encrypt', 'decrypt']
        );
    }

    /**
     * Generates a device-specific key for encryption
     * Uses a combination of user agent and a stored device ID
     * @returns {Promise<CryptoKey>} The encryption key
     */
    static async getEncryptionKey() {
        // Generate or retrieve a device-specific ID
        let deviceId = localStorage.getItem('_deviceId');
        if (!deviceId) {
            // Generate a random device ID
            deviceId = crypto.getRandomValues(new Uint8Array(16))
                .reduce((str, byte) => str + byte.toString(16).padStart(2, '0'), '');
            localStorage.setItem('_deviceId', deviceId);
        }

        // Create a password from device ID and user agent
        const password = `${deviceId}_${navigator.userAgent}_metapath_secure_storage`;
        
        // Use a fixed salt derived from the password (for consistency)
        // In production, consider using a server-provided salt
        const salt = new Uint8Array(
            await crypto.subtle.digest('SHA-256', new TextEncoder().encode(password))
        ).slice(0, 16);

        return this.deriveKey(password, salt);
    }

    /**
     * Encrypts data using AES-GCM
     * @param {string} data - The data to encrypt
     * @param {CryptoKey} key - The encryption key
     * @returns {Promise<string>} Base64-encoded encrypted data with IV
     */
    static async encrypt(data, key) {
        const encoder = new TextEncoder();
        const dataBuffer = encoder.encode(data);
        
        // Generate a random IV for each encryption
        const iv = crypto.getRandomValues(new Uint8Array(12));
        
        // Encrypt the data
        const encrypted = await crypto.subtle.encrypt(
            {
                name: 'AES-GCM',
                iv: iv
            },
            key,
            dataBuffer
        );

        // Combine IV and encrypted data
        const combined = new Uint8Array(iv.length + encrypted.byteLength);
        combined.set(iv, 0);
        combined.set(new Uint8Array(encrypted), iv.length);

        // Convert to base64 for storage
        return btoa(String.fromCharCode(...combined));
    }

    /**
     * Decrypts data using AES-GCM
     * @param {string} encryptedData - Base64-encoded encrypted data with IV
     * @param {CryptoKey} key - The decryption key
     * @returns {Promise<string>} The decrypted data
     */
    static async decrypt(encryptedData, key) {
        try {
            // Convert from base64
            const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
            
            // Extract IV and encrypted data
            const iv = combined.slice(0, 12);
            const encrypted = combined.slice(12);

            // Decrypt the data
            const decrypted = await crypto.subtle.decrypt(
                {
                    name: 'AES-GCM',
                    iv: iv
                },
                key,
                encrypted
            );

            const decoder = new TextDecoder();
            return decoder.decode(decrypted);
        } catch (error) {
            console.error('Decryption error:', error);
            throw new Error('Failed to decrypt data. Data may be corrupted or key may be invalid.');
        }
    }

    /**
     * Stores encrypted data in localStorage
     * @param {string} key - The storage key
     * @param {any} value - The value to store (will be JSON stringified and encrypted)
     * @returns {Promise<void>}
     */
    static async setItem(key, value) {
        try {
            const encryptionKey = await this.getEncryptionKey();
            const dataString = JSON.stringify(value);
            const encrypted = await this.encrypt(dataString, encryptionKey);
            localStorage.setItem(key, encrypted);
        } catch (error) {
            console.error(`Error storing encrypted data for key ${key}:`, error);
            throw error;
        }
    }

    /**
     * Retrieves and decrypts data from localStorage
     * @param {string} key - The storage key
     * @returns {Promise<any|null>} The decrypted value or null if not found
     */
    static async getItem(key) {
        try {
            const encrypted = localStorage.getItem(key);
            if (!encrypted) {
                return null;
            }

            const encryptionKey = await this.getEncryptionKey();
            const decrypted = await this.decrypt(encrypted, encryptionKey);
            return JSON.parse(decrypted);
        } catch (error) {
            console.error(`Error retrieving encrypted data for key ${key}:`, error);
            // If decryption fails, clear the corrupted data
            localStorage.removeItem(key);
            return null;
        }
    }

    /**
     * Removes an item from localStorage
     * @param {string} key - The storage key
     */
    static removeItem(key) {
        localStorage.removeItem(key);
    }

    /**
     * Clears all encrypted data from localStorage
     * Note: This does NOT clear the device ID
     */
    static clear() {
        // Get all keys
        const keys = Object.keys(localStorage);
        
        // Remove all keys except the device ID
        keys.forEach(key => {
            if (key !== '_deviceId') {
                localStorage.removeItem(key);
            }
        });
    }

    /**
     * Checks if Web Crypto API is available
     * @returns {boolean}
     */
    static isSupported() {
        return typeof crypto !== 'undefined' && 
               typeof crypto.subtle !== 'undefined' &&
               typeof crypto.getRandomValues !== 'undefined';
    }
}

export default SecureStorage;

